import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/** Cursor is inside an incomplete `{{ …` segment (no closing `}}` yet). */
export function getMustacheCompletionContext(
  text: string,
  cursor: number
): { braceStart: number; filter: string } | null {
  const before = text.slice(0, cursor);
  const open = before.lastIndexOf('{{');
  if (open === -1) return null;
  const afterOpen = before.slice(open + 2);
  if (afterOpen.includes('}}')) return null;
  return { braceStart: open, filter: afterOpen };
}

export function suggestionMatchInner(suggestion: string, filter: string): boolean {
  const inner = suggestion
    .replace(/^\{\{\s*/, '')
    .replace(/\s*\}\}\s*$/, '')
    .trim()
    .toLowerCase();
  const f = filter.trim().toLowerCase();
  if (!f) return true;
  return inner.startsWith(f);
}

function getTextareaCaretClientPosition(
  textarea: HTMLTextAreaElement,
  position: number,
  valueOverride?: string
): { top: number; left: number; height: number } {
  const rect = textarea.getBoundingClientRect();
  const cs = window.getComputedStyle(textarea);
  const mirror = document.createElement('div');
  mirror.setAttribute('aria-hidden', 'true');
  const copyProps = [
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'letterSpacing',
    'textTransform',
    'textAlign',
    'direction',
    'lineHeight',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'boxSizing',
    'wordBreak',
    'overflowWrap',
    'tabSize',
  ] as const;
  for (const p of copyProps) {
    mirror.style.setProperty(p, cs.getPropertyValue(p));
  }
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordWrap = 'break-word';
  mirror.style.visibility = 'hidden';
  mirror.style.position = 'fixed';
  mirror.style.pointerEvents = 'none';
  mirror.style.left = '0';
  mirror.style.top = '0';
  mirror.style.zIndex = '-1';
  mirror.style.overflow = 'hidden';
  mirror.style.width = `${textarea.clientWidth}px`;

  const fullValue = valueOverride ?? textarea.value;
  const before = fullValue.slice(0, Math.max(0, position));
  mirror.appendChild(document.createTextNode(before));
  const marker = document.createElement('span');
  marker.textContent = '\u200b';
  mirror.appendChild(marker);
  document.body.appendChild(mirror);

  const mt = marker.offsetTop;
  const ml = marker.offsetLeft;
  const mh = marker.offsetHeight || parseFloat(cs.lineHeight) || 18;
  document.body.removeChild(mirror);

  const st = textarea.scrollTop;
  const sl = textarea.scrollLeft;
  const bl = parseFloat(cs.borderLeftWidth) || 0;
  const bt = parseFloat(cs.borderTopWidth) || 0;
  const pl = parseFloat(cs.paddingLeft) || 0;
  const pt = parseFloat(cs.paddingTop) || 0;

  return {
    top: rect.top + bt + pt + mt - st,
    left: rect.left + bl + pl + ml - sl,
    height: mh,
  };
}

export function layoutPromptSuggestMenuAtCaret(
  textarea: HTMLTextAreaElement,
  cursor: number,
  valueOverride?: string
): { top: number; left: number; maxHeight: number } {
  const c = getTextareaCaretClientPosition(textarea, cursor, valueOverride);
  const gap = 4;
  const belowTop = c.top + c.height + gap;
  const spaceBelow = window.innerHeight - belowTop - 10;
  const spaceAbove = c.top - 10;
  const minH = 100;
  const preferBelow = spaceBelow >= minH || spaceBelow >= spaceAbove;
  const maxDefault = 220;
  let top: number;
  let maxHeight: number;
  if (preferBelow) {
    top = belowTop;
    maxHeight = Math.min(maxDefault, Math.max(minH, spaceBelow));
  } else {
    maxHeight = Math.min(maxDefault, Math.max(minH, spaceAbove - gap));
    top = Math.max(8, c.top - gap - maxHeight);
  }
  const menuMinW = 200;
  const left = Math.max(8, Math.min(c.left, window.innerWidth - menuMinW - 8));
  return { top, left, maxHeight };
}

export type MustacheSuggestClassNames = {
  wrap: string;
  suggestList: string;
  suggestListAtCaret: string;
  suggestItem: string;
  suggestItemActive: string;
};

export const MUSTACHE_SUGGEST_CN_LLM: MustacheSuggestClassNames = {
  wrap: 'workflow-llm-panel__prompt-suggest-wrap',
  suggestList: 'workflow-llm-panel__prompt-suggest',
  suggestListAtCaret: 'workflow-llm-panel__prompt-suggest workflow-llm-panel__prompt-suggest--at-caret',
  suggestItem: 'workflow-llm-panel__prompt-suggest-item',
  suggestItemActive: 'workflow-llm-panel__prompt-suggest-item workflow-llm-panel__prompt-suggest-item--active',
};

export const MUSTACHE_SUGGEST_CN_ACTION: MustacheSuggestClassNames = {
  wrap: 'workflow-action-panel__mustache-wrap',
  suggestList: 'workflow-action-panel__mustache-suggest',
  suggestListAtCaret: 'workflow-action-panel__mustache-suggest workflow-action-panel__mustache-suggest--at-caret',
  suggestItem: 'workflow-action-panel__mustache-suggest-item',
  suggestItemActive:
    'workflow-action-panel__mustache-suggest-item workflow-action-panel__mustache-suggest-item--active',
};

export type MustacheSuggestTextareaProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange'
> & {
  value: string;
  onChange: (next: string) => void;
  suggestions: string[];
  /** When this value changes, the suggestion menu is closed and internal refs reset. */
  resetKey?: string;
  classNames?: Partial<MustacheSuggestClassNames>;
};

/**
 * Textarea with caret-anchored `{{` variable suggestions (same behavior as the LLM parent prompt field).
 */
export function MustacheSuggestTextarea({
  value,
  onChange,
  suggestions,
  resetKey,
  classNames: classNamesPartial,
  className,
  onKeyDown,
  ...textareaRest
}: MustacheSuggestTextareaProps) {
  const cn = useMemo(
    () => ({ ...MUSTACHE_SUGGEST_CN_LLM, ...classNamesPartial }),
    [classNamesPartial]
  );

  const wrapRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const replaceRangeRef = useRef<{ from: number; to: number } | null>(null);
  const liveRef = useRef<{ items: string[]; index: number }>({ items: [], index: 0 });
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [menuGeom, setMenuGeom] = useState<{ top: number; left: number; maxHeight: number } | null>(null);

  const suggestionList = useMemo(() => Array.from(new Set(suggestions.filter(Boolean))), [suggestions]);

  const updateMenuGeom = useCallback(() => {
    const ta = taRef.current;
    if (!ta || !open) {
      setMenuGeom(null);
      return;
    }
    const cursor = ta.selectionStart ?? 0;
    setMenuGeom(layoutPromptSuggestMenuAtCaret(ta, cursor));
  }, [open]);

  const refresh = useCallback(
    (text: string, cursor: number) => {
      const ctx = getMustacheCompletionContext(text, cursor);
      if (!ctx) {
        replaceRangeRef.current = null;
        liveRef.current = { items: [], index: 0 };
        setOpen(false);
        setItems([]);
        setMenuGeom(null);
        return;
      }
      const nextItems = suggestionList.filter((s) => suggestionMatchInner(s, ctx.filter));
      if (nextItems.length === 0) {
        replaceRangeRef.current = null;
        liveRef.current = { items: [], index: 0 };
        setOpen(false);
        setItems([]);
        setMenuGeom(null);
        return;
      }
      replaceRangeRef.current = { from: ctx.braceStart, to: cursor };
      liveRef.current = { items: nextItems, index: 0 };
      setItems(nextItems);
      setIndex(0);
      setOpen(true);
      const taNow = taRef.current;
      if (taNow) setMenuGeom(layoutPromptSuggestMenuAtCaret(taNow, cursor, text));
    },
    [suggestionList]
  );

  const applyPick = useCallback(
    (template: string) => {
      const ta = taRef.current;
      const range = replaceRangeRef.current;
      if (!ta || !range) return;
      const v = ta.value;
      const replaceEnd = ta.selectionStart ?? v.length;
      const newText = v.slice(0, range.from) + template + v.slice(replaceEnd);
      onChange(newText);
      replaceRangeRef.current = null;
      liveRef.current = { items: [], index: 0 };
      setOpen(false);
      setItems([]);
      setMenuGeom(null);
      const caret = range.from + template.length;
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(caret, caret);
      });
    },
    [onChange]
  );

  useLayoutEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-suggest-idx="${index}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [index, open]);

  useLayoutEffect(() => {
    updateMenuGeom();
  }, [updateMenuGeom, value, items, open]);

  useEffect(() => {
    if (!open) return;
    const ta = taRef.current;
    const onReposition = () => updateMenuGeom();
    window.addEventListener('resize', onReposition);
    ta?.addEventListener('scroll', onReposition, { passive: true });
    return () => {
      window.removeEventListener('resize', onReposition);
      ta?.removeEventListener('scroll', onReposition);
    };
  }, [open, updateMenuGeom]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (ev: MouseEvent) => {
      const root = wrapRef.current;
      if (root && ev.target instanceof Node && root.contains(ev.target)) return;
      setOpen(false);
      setItems([]);
      setMenuGeom(null);
      replaceRangeRef.current = null;
      liveRef.current = { items: [], index: 0 };
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEffect(() => {
    liveRef.current.index = index;
  }, [index]);

  useEffect(() => {
    liveRef.current.items = items;
  }, [items]);

  useEffect(() => {
    setOpen(false);
    setItems([]);
    setMenuGeom(null);
    replaceRangeRef.current = null;
    liveRef.current = { items: [], index: 0 };
  }, [resetKey]);

  const mergedKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.stopPropagation();
    }
    if (!open || items.length === 0) {
      onKeyDown?.(e);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndex((i) => {
        const next = Math.min(liveRef.current.items.length - 1, i + 1);
        liveRef.current.index = next;
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((i) => {
        const next = Math.max(0, i - 1);
        liveRef.current.index = next;
        return next;
      });
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const { items: liveItems, index: liveIndex } = liveRef.current;
      const pick = liveItems[liveIndex];
      if (pick) applyPick(pick);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const { items: liveItems, index: liveIndex } = liveRef.current;
      const pick = liveItems[liveIndex];
      if (pick) applyPick(pick);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setItems([]);
      setMenuGeom(null);
      replaceRangeRef.current = null;
      liveRef.current = { items: [], index: 0 };
    } else {
      onKeyDown?.(e);
    }
  };

  return (
    <div ref={wrapRef} className={cn.wrap}>
      <textarea
        ref={taRef}
        {...textareaRest}
        className={className}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          const cursor = e.target.selectionStart ?? e.target.value.length;
          refresh(e.target.value, cursor);
        }}
        onKeyDown={mergedKeyDown}
        onClick={(e) => refresh(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)}
        onKeyUp={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') return;
          refresh(e.currentTarget.value, e.currentTarget.selectionStart ?? 0);
        }}
      />
      {open && items.length > 0 && menuGeom ? (
        <div
          ref={listRef}
          className={cn.suggestListAtCaret}
          style={{
            top: menuGeom.top,
            left: menuGeom.left,
            maxHeight: menuGeom.maxHeight,
          }}
          role="listbox"
          aria-label="Variable suggestions"
        >
          {items.map((item, idx) => (
            <button
              key={item}
              type="button"
              role="option"
              aria-selected={idx === index}
              data-suggest-idx={idx}
              className={idx === index ? cn.suggestItemActive : cn.suggestItem}
              onMouseDown={(ev) => {
                ev.preventDefault();
                applyPick(item);
              }}
              onMouseEnter={() => {
                setIndex(idx);
                liveRef.current.index = idx;
              }}
            >
              {item}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
